import { Directive, EventEmitter, Input, NgModule, OnDestroy, Output } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Emits `lastItemReached` when the user scrolls near the bottom of a `mat-select`
 * overlay panel. Intended for driving "load more" pagination on long dropdowns.
 *
 * Usage:
 *   <mat-select tsMatSelectInfiniteScroll (lastItemReached)="loadMore()">
 */
@Directive({
  selector: 'mat-select[tsMatSelectInfiniteScroll]',
  standalone: true,
})
export class MatSelectInfiniteScrollDirective implements OnDestroy {
  /** Distance (in px) from the bottom at which the emission fires. */
  @Input() threshold = 5;

  @Output() lastItemReached = new EventEmitter<void>();

  private readonly destroyed$ = new Subject<void>();
  private cleanup?: () => void;

  constructor(private readonly select: MatSelect) {
    this.select.openedChange.pipe(takeUntil(this.destroyed$)).subscribe((opened) => {
      if (opened) {
        // Material 18+ MDC select fires openedChange BEFORE the CDK overlay
        // is attached and the @ViewChild('panel') ref is populated. Retry
        // attach with requestAnimationFrame until panel becomes available
        // (or give up after a few frames).
        this.tryAttach(10);
      } else {
        this.detach();
      }
    });
  }

  ngOnDestroy(): void {
    this.detach();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private tryAttach(attemptsLeft: number): void {
    if (this.cleanup) return; // already attached, idempotent
    const el = this.select.panel?.nativeElement as HTMLElement | undefined;
    if (el) {
      this.attachListener(el);
      return;
    }
    if (attemptsLeft > 0) {
      requestAnimationFrame(() => this.tryAttach(attemptsLeft - 1));
    }
  }

  private attachListener(el: HTMLElement): void {
    const onScroll = (event: Event) => {
      const { scrollTop, clientHeight, scrollHeight } = event.target as HTMLElement;
      if (scrollTop + clientHeight >= scrollHeight - this.threshold) {
        this.lastItemReached.emit();
      }
    };
    el.addEventListener('scroll', onScroll);
    this.cleanup = () => el.removeEventListener('scroll', onScroll);
  }

  private detach(): void {
    this.cleanup?.();
    this.cleanup = undefined;
  }
}

/**
 * Convenience NgModule for non-standalone consumers.
 * Standalone consumers can import `MatSelectInfiniteScrollDirective` directly.
 */
@NgModule({
  imports: [MatSelectInfiniteScrollDirective],
  exports: [MatSelectInfiniteScrollDirective],
})
export class MatSelectInfiniteScrollDirectiveModule {}
